import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, Copy, ChevronDown, ChevronUp, XCircle, CheckCircle2,
  TrendingUp, Zap, Target, Sparkles, X, ListChecks, Loader2,
  FileCode, Type, Image, Link2, Settings, Play, ExternalLink,
  MapPin, ArrowRight, Code2, Bot,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { toast } from 'sonner';

/* ─── Config ─────────────────────────────────────────────────────────────── */
export const CATEGORY_CONFIG = {
  meta_tags: { icon: FileCode, label: 'Meta Tags', color: 'bg-violet-100 text-violet-700' },
  headings: { icon: Type, label: 'Headings', color: 'bg-blue-100 text-blue-700' },
  content: { icon: FileCode, label: 'Content', color: 'bg-emerald-100 text-emerald-700' },
  images: { icon: Image, label: 'Images', color: 'bg-amber-100 text-amber-700' },
  links: { icon: Link2, label: 'Links', color: 'bg-cyan-100 text-cyan-700' },
  technical: { icon: Settings, label: 'Technical', color: 'bg-slate-100 text-slate-700' },
  general: { icon: Settings, label: 'General', color: 'bg-slate-100 text-slate-700' },
};

export const SEVERITY_CONFIG = {
  high: { color: 'bg-red-100 text-red-700 border-red-200', label: 'High Priority' },
  medium: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Medium' },
  low: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Low Priority' },
};

export const STATUS_CONFIG = {
  pending: { color: 'bg-slate-100 text-slate-700', label: 'Pending Client Review' },
  client_approved: { color: 'bg-blue-100 text-blue-700', label: 'Awaiting Final Approval' },
  approved: { color: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
  implemented: { color: 'bg-indigo-100 text-indigo-700', label: 'Implemented' },
};

export const PRIORITY_CONFIG = {
  critical: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Critical' },
  important: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Important' },
  minor: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Minor' },
  quick_win: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Quick Win' },
};

/* ─── RecommendationCard ─────────────────────────────────────────────────── */
export const RecommendationCard = ({ rec, onApprove, onReject, onImplement, onExecute, isClient, canFinalApprove = false, canImplement = false, scanUrl, highlight = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  /* Auto-expand when deep-linked from a notification. */
  useEffect(() => { if (highlight) setExpanded(true); }, [highlight]);
  const [executing, setExecuting] = useState(false);
  const category = CATEGORY_CONFIG[rec.category] || CATEGORY_CONFIG.general;
  const severity = SEVERITY_CONFIG[rec.severity] || SEVERITY_CONFIG.medium;
  const status = STATUS_CONFIG[rec.status] || STATUS_CONFIG.pending;
  const CategoryIcon = category.icon;

  const handleCopy = () => {
    if (rec.code_snippet) {
      navigator.clipboard.writeText(rec.code_snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Code copied to clipboard!');
    }
  };

  const handleExecute = async (e) => {
    e.stopPropagation();
    if (!onExecute) return;
    setExecuting(true);
    try {
      await onExecute(rec.recommendation_id);
    } catch {
      // error already handled by parent
    } finally {
      setExecuting(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-xl overflow-hidden bg-white dark:bg-slate-900 transition-shadow ${
        highlight
          ? 'border-indigo-400 dark:border-indigo-500 ring-2 ring-indigo-400 ring-offset-2 dark:ring-offset-slate-900'
          : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      <div
        className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-xl ${category.color} flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold">{rec.title}</h3>
              <Badge variant="outline" className={severity.color}>{rec.severity}</Badge>
              <Badge variant="outline" className={status.color}>{status.label}</Badge>
              {rec.priority_group && PRIORITY_CONFIG[rec.priority_group] && (
                <Badge variant="outline" className={PRIORITY_CONFIG[rec.priority_group].color}>
                  {rec.priority_group === 'quick_win' && <Zap className="w-3 h-3 mr-1" />}
                  {PRIORITY_CONFIG[rec.priority_group].label}
                </Badge>
              )}
              {rec.effort && (
                <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                  {rec.effort} effort
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 flex-1">
                {rec.description || rec.why_it_matters}
              </p>
              {rec.estimated_impact && (
                <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 whitespace-nowrap flex-shrink-0">
                  <TrendingUp className="w-3.5 h-3.5" />
                  {rec.estimated_impact}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Stage 1 (client) acts on pending; Stage 2 (final approver) acts on pending + client_approved.
                AI-executable recs skip approval entirely and offer a direct Execute instead. */}
            {((isClient && rec.status === 'pending')
              || (canFinalApprove && (rec.status === 'pending' || rec.status === 'client_approved'))) && (
              <>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); onReject(rec.recommendation_id); }}>
                  <XCircle className="w-4 h-4" />
                </Button>
                {rec.can_auto_execute ? (
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={handleExecute} disabled={executing}>
                    {executing
                      ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Executing</>
                      : <><Zap className="w-4 h-4 mr-1" />Direct Execute</>}
                  </Button>
                ) : (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={(e) => { e.stopPropagation(); onApprove(rec.recommendation_id); }}>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    {canFinalApprove && rec.status === 'client_approved' ? 'Final Approve' : 'Approve'}
                  </Button>
                )}
              </>
            )}
            {/* Client waiting on internal final review */}
            {isClient && rec.status === 'client_approved' && (
              <Badge variant="outline" className="bg-blue-100 text-blue-700">Awaiting Final Approval</Badge>
            )}
            {rec.status === 'approved' && rec.can_auto_execute && (isClient || canImplement) && (
              <div className="flex items-center gap-1">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={handleExecute} disabled={executing}>
                  {executing
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Executing</>
                    : <><Play className="w-4 h-4 mr-1" />Execute</>}
                </Button>
                {canImplement && (
                  <Button size="sm" variant="outline"
                    onClick={(e) => { e.stopPropagation(); onImplement(rec.recommendation_id); }}>
                    <Check className="w-4 h-4 mr-1" />Done
                  </Button>
                )}
              </div>
            )}
            {rec.status === 'approved' && !rec.can_auto_execute && canImplement && (
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={(e) => { e.stopPropagation(); onImplement(rec.recommendation_id); }}>
                <Check className="w-4 h-4 mr-1" />
                Mark Done
              </Button>
            )}
            {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-slate-100 dark:border-slate-800"
          >
            <div className="p-4 space-y-4 bg-slate-50/50 dark:bg-slate-800/50">
              {/* Element Location + View on Page */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="w-4 h-4 text-indigo-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400">Found in</p>
                    <p className="text-sm text-indigo-800 dark:text-indigo-300 truncate">
                      {rec.element_location?.description || `${CATEGORY_CONFIG[rec.category]?.label || rec.category} section`}
                    </p>
                    {rec.element_location?.selector && (
                      <code className="text-[11px] text-indigo-500 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded mt-0.5 inline-block font-mono">
                        {rec.element_location.selector}
                      </code>
                    )}
                  </div>
                </div>
                {(rec.element_location?.page_url || scanUrl) && (
                  <a
                    href={rec.element_location?.page_url || scanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 bg-white dark:bg-indigo-900/40 px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-700 hover:border-indigo-300 transition-colors shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View on Page
                  </a>
                )}
              </div>

              {/* Before / After Visual Diff */}
              {rec.current_value && rec.recommended_fix && (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="grid grid-cols-[1fr_auto_1fr] bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <span className="text-red-600">Before</span>
                    <span></span>
                    <span className="text-emerald-600">After</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] items-stretch">
                    <div className="p-3 bg-red-50/50 dark:bg-red-900/10 border-r border-slate-200 dark:border-slate-700">
                      <p className="text-sm text-red-800 dark:text-red-300 break-words">{rec.current_value}</p>
                    </div>
                    <div className="flex items-center px-2">
                      <ArrowRight className="w-4 h-4 text-slate-300" />
                    </div>
                    <div className="p-3 bg-emerald-50/50 dark:bg-emerald-900/10">
                      <p className="text-sm text-emerald-800 dark:text-emerald-300 break-words">{rec.recommended_fix}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Affected images (if any) */}
              {rec.element_location?.examples?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase mb-1.5">Affected Elements</p>
                  <div className="space-y-1.5">
                    {rec.element_location.examples.map((ex, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                        <Image className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="text-slate-600 dark:text-slate-400 truncate font-mono text-xs">
                          {ex.src ? ex.src.split('/').pop() : 'unknown'}
                        </span>
                        {!ex.alt && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-red-50 text-red-600 border-red-200 shrink-0">
                            missing alt
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Value (standalone, for recs without a diff) */}
              {rec.current_value && !rec.recommended_fix && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase mb-1">Current Value</p>
                  <p className="text-sm bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-700">{rec.current_value}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-medium text-slate-500 uppercase mb-1">Why This Matters</p>
                <p className="text-sm bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
                  {rec.why_it_matters}
                </p>
              </div>

              {/* Implementation Status (for executed recs) */}
              {rec.status === 'implemented' && (
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                      {rec.auto_executed ? 'Auto-executed and applied to your site' : 'Marked as implemented'}
                    </span>
                  </div>
                  {rec.implemented_at && (
                    <p className="text-xs text-emerald-600 mt-1 ml-6">
                      {new Date(rec.implemented_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {/* AI Execution Preview (shown before execution for auto-executable items) */}
              {rec.can_auto_execute && rec.status !== 'implemented' && rec.execution_preview && (
                <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 border-b border-indigo-200 dark:border-indigo-800">
                    <Bot className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">
                      What AI Will Do
                    </span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-indigo-100 text-indigo-600 border-indigo-300 dark:bg-indigo-900/50 dark:text-indigo-400 dark:border-indigo-700 ml-auto">
                      Auto-Executable
                    </Badge>
                  </div>
                  <div className="p-3 space-y-2.5 bg-white dark:bg-slate-900/50">
                    <p className="text-sm text-slate-700 dark:text-slate-300">{rec.execution_preview.summary}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-800">
                        <span className="text-slate-400 font-medium">Target</span>
                        <p className="text-slate-700 dark:text-slate-300 mt-0.5">{rec.execution_preview.target}</p>
                      </div>
                      <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-800">
                        <span className="text-slate-400 font-medium">Action</span>
                        <p className="text-slate-700 dark:text-slate-300 mt-0.5 capitalize">{rec.execution_preview.change_type}</p>
                      </div>
                      <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-800 col-span-2">
                        <span className="text-slate-400 font-medium">Method</span>
                        <p className="text-slate-700 dark:text-slate-300 mt-0.5">{rec.execution_preview.method}</p>
                      </div>
                      {rec.execution_preview.will_modify && (
                        <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-800 col-span-2">
                          <span className="text-slate-400 font-medium">Page</span>
                          <p className="text-slate-700 dark:text-slate-300 mt-0.5 font-mono text-[11px]">{rec.execution_preview.will_modify}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {rec.code_snippet && (rec.can_auto_execute || rec.status === 'approved' || rec.status === 'implemented' || !isClient) && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-slate-500 uppercase flex items-center gap-1.5">
                      <Code2 className="w-3.5 h-3.5" />
                      Implementation Code
                    </p>
                    <Button variant="ghost" size="sm" onClick={handleCopy}>
                      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      <span className="ml-1">{copied ? 'Copied!' : 'Copy'}</span>
                    </Button>
                  </div>
                  <pre className="text-sm bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto font-mono">
                    {rec.code_snippet}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─── ScanFormDialog ─────────────────────────────────────────────────────── */
export const ScanFormDialog = ({ open, onClose, onSubmit, scanning, websiteUrl }) => {
  const [targetKeyword, setTargetKeyword] = useState('');
  const [pageType, setPageType] = useState('');
  const [secondaryKeywords, setSecondaryKeywords] = useState('');

  if (!open) return null;

  const buildFormData = () => ({
    target_keyword: targetKeyword || undefined,
    page_type: pageType || undefined,
    secondary_keywords: secondaryKeywords
      ? secondaryKeywords.split(',').map((k) => k.trim()).filter(Boolean)
      : undefined,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(buildFormData());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-600" />
            Configure Scan
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-1">
          Scanning <strong>{websiteUrl}</strong>
        </p>
        <p className="text-xs text-slate-400 mb-4">
          These settings are optional. Add target keywords for keyword-specific analysis, or leave blank for a general SEO audit.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Target Keyword</label>
            <div className="relative">
              <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={targetKeyword}
                onChange={(e) => setTargetKeyword(e.target.value)}
                placeholder="e.g. best project management software"
                className="w-full pl-10 pr-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Page Type</label>
            <select
              value={pageType}
              onChange={(e) => setPageType(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Auto-detect</option>
              <option value="blog">Blog Post</option>
              <option value="product">Product Page</option>
              <option value="landing">Landing Page</option>
              <option value="service">Service Page</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Secondary Keywords <span className="text-slate-400">(comma-separated)</span></label>
            <input
              type="text"
              value={secondaryKeywords}
              onChange={(e) => setSecondaryKeywords(e.target.value)}
              placeholder="e.g. task management, team collaboration"
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            {onSubmitStepByStep && (
              <Button type="button" variant="outline" onClick={handleStepByStep} disabled={scanning}>
                <ListChecks className="w-4 h-4 mr-2" />Step-by-Step
              </Button>
            )}
            <Button type="submit" className="gds-btn-primary" disabled={scanning}>
              {scanning
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                : <><Sparkles className="w-4 h-4 mr-2" />Run Scan</>}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

/* ─── RecommendationsPanel (recommendations card + tabs + pagination) ───── */
export const RecommendationsPanel = ({
  recommendations = [],
  scanUrl,
  onApprove,
  onReject,
  onImplement,
  onExecute,
  isClient,
  canFinalApprove = false,
  canImplement = false,
  stats,
  highlightRecId,
}) => {
  const [viewMode, setViewMode] = useState('priority');
  const [priorityTab, setPriorityTab] = useState('all');
  const [statusTab, setStatusTab] = useState('all');
  const [recPage, setRecPage] = useState(1);
  const REC_PER_PAGE = 5;

  /* When deep-linked to a specific recommendation, reset to the unfiltered
     priority view and jump to the page that contains it so it's visible. */
  useEffect(() => {
    if (!highlightRecId) return;
    const idx = recommendations.findIndex((r) => r.recommendation_id === highlightRecId);
    if (idx < 0) return;
    setViewMode('priority');
    setPriorityTab('all');
    setRecPage(Math.floor(idx / REC_PER_PAGE) + 1);
  }, [highlightRecId, recommendations]);

  const filtered = recommendations.filter((r) => {
    if (viewMode === 'priority') {
      return priorityTab === 'all' || r.priority_group === priorityTab;
    }
    return statusTab === 'all'
      || (statusTab === 'pending' && r.status === 'pending')
      || (statusTab === 'client_approved' && r.status === 'client_approved')
      || (statusTab === 'approved' && r.status === 'approved')
      || (statusTab === 'implemented' && r.status === 'implemented');
  });

  const totalPages = Math.ceil(filtered.length / REC_PER_PAGE);
  const paginated = filtered.slice((recPage - 1) * REC_PER_PAGE, recPage * REC_PER_PAGE);

  return (
    <Card className="gds-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <CardTitle className="font-heading">AI Recommendations</CardTitle>
            <CardDescription>
              {isClient ? 'Review and approve changes for your website' : 'Approved changes ready for implementation'}
            </CardDescription>
          </div>
          <Tabs value={viewMode} onValueChange={(val) => { setViewMode(val); setRecPage(1); }}>
            <TabsList className="bg-slate-100 dark:bg-slate-800">
              <TabsTrigger value="priority">Priority View</TabsTrigger>
              <TabsTrigger value="status">Status View</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {viewMode === 'priority' && (
          <Tabs value={priorityTab} onValueChange={(val) => { setPriorityTab(val); setRecPage(1); }}>
            <TabsList className="bg-slate-100 dark:bg-slate-800 flex-wrap">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="critical">
                Critical
                {stats?.critical > 0 && <Badge variant="destructive" className="ml-1 h-5 min-w-[1.25rem] px-1 text-xs">{stats.critical}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="important">
                Important
                {stats?.important > 0 && <Badge className="ml-1 h-5 min-w-[1.25rem] px-1 text-xs bg-amber-500">{stats.important}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="minor">Minor</TabsTrigger>
              <TabsTrigger value="quick_win">
                <Zap className="w-3.5 h-3.5 mr-1" />Quick Wins
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {viewMode === 'status' && (
          <Tabs value={statusTab} onValueChange={(val) => { setStatusTab(val); setRecPage(1); }}>
            <TabsList className="bg-slate-100 dark:bg-slate-800">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="pending">
                Pending
                {stats?.pending > 0 && <Badge variant="destructive" className="ml-1 h-5 min-w-[1.25rem] px-1 text-xs">{stats.pending}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="client_approved">
                Awaiting Final
                {stats?.client_approved > 0 && <Badge className="ml-1 h-5 min-w-[1.25rem] px-1 text-xs bg-blue-500">{stats.client_approved}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="implemented">Done</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No recommendations in this category</p>
          </div>
        ) : (
          <>
            {paginated.map((rec) => (
              <RecommendationCard
                key={rec.recommendation_id}
                rec={rec}
                isClient={isClient}
                canFinalApprove={canFinalApprove}
                canImplement={canImplement}
                scanUrl={scanUrl}
                onApprove={onApprove}
                onReject={onReject}
                onImplement={onImplement}
                onExecute={onExecute}
                highlight={rec.recommendation_id === highlightRecId}
              />
            ))}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-500">
                  Showing {(recPage - 1) * REC_PER_PAGE + 1}–{Math.min(recPage * REC_PER_PAGE, filtered.length)} of {filtered.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={recPage === 1}
                    onClick={() => setRecPage((p) => p - 1)}>
                    ‹
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Button key={i + 1} variant={recPage === i + 1 ? 'default' : 'outline'}
                      size="sm" className="h-8 w-8 p-0" onClick={() => setRecPage(i + 1)}>
                      {i + 1}
                    </Button>
                  ))}
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={recPage === totalPages}
                    onClick={() => setRecPage((p) => p + 1)}>
                    ›
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
